import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
  Visibility as VisibilityIcon,
  DateRange as DateRangeIcon,
  Storage as StorageIcon,
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import axios from "axios";
import { collection, getDocs, query, where, orderBy, Timestamp } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import FUNCTIONS_URLS from '../../../../Config/functions';


const ProductionMigrationTool = () => {
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [migrationInfo, setMigrationInfo] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [currentStep, setCurrentStep] = useState('');
  const [canCancel, setCanCancel] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [expandedRowyOnly, setExpandedRowyOnly] = useState(null);
  const [selectedRowyOnlyDocs, setSelectedRowyOnlyDocs] = useState({}); // { collectionKey: [docIds] }
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const isExecutingRef = useRef(false);
  
  const handleSelectRowyOnlyDoc = (collectionKey, docId) => {
    setSelectedRowyOnlyDocs(prev => {
      const newSet = new Set(prev[collectionKey] || []);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return { ...prev, [collectionKey]: Array.from(newSet) };
    });
  };
  
  const handleSelectAllInRisk = (collectionKey, docs, riskLevel) => {
    const docIds = docs.map(d => d.id);
    setSelectedRowyOnlyDocs(prev => {
      const current = new Set(prev[collectionKey] || []);
      const allSelected = docIds.every(id => current.has(id));
      if (allSelected) {
        // Deselect all
        docIds.forEach(id => current.delete(id));
      } else {
        // Select all
        docIds.forEach(id => current.add(id));
      }
      return { ...prev, [collectionKey]: Array.from(current) };
    });
  };
  
  const getSelectedCount = (collectionKey) => {
    return (selectedRowyOnlyDocs[collectionKey] || []).length;
  };

  // Configuration state
  const [startDate, setStartDate] = useState(dayjs("2025-12-22"));
  const [endDate, setEndDate] = useState(dayjs("2026-01-05"));
  const [selectedCollections, setSelectedCollections] = useState({
    users: true,
    ledgers: true,
    monthly_grpahs: true,
    monthly_closings: true,
    transection_requests: true,
    yearly_graph: false,
    notifications: false,
  });
  const [updateBalance, setUpdateBalance] = useState(true);
  const [syncTransactionStatus, setSyncTransactionStatus] = useState(true);
  const [importPerformance, setImportPerformance] = useState(false);
  const [userSelectionMode, setUserSelectionMode] = useState("all"); // "all" or "specific"
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  // Load available users
  useEffect(() => {
    loadAvailableUsers();
  }, []);

  const loadAvailableUsers = async () => {
    try {
      const usersSnapshot = await getDocs(
        query(collection(DATABASE, "users"), where("role", "==", 1), orderBy("full_name"))
      );
      const users = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        email: doc.data().email,
        full_name: doc.data().full_name,
      }));
      setAvailableUsers(users);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };


  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const selectedCols = Object.keys(selectedCollections).filter(
        (key) => selectedCollections[key]
      );
      
      if (selectedCols.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Collections Selected',
          text: 'Please select at least one collection to preview.',
        });
        setPreviewLoading(false);
        return;
      }
      
      // Call Cloud Function to get preview with date range
      const response = await axios.post(
        FUNCTIONS_URLS.PreviewProductionMigration,
        { 
          collections: selectedCols,
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
          importPerformance: importPerformance
        },
        { 
          headers: { 'Content-Type': 'application/json' },
          timeout: 300000 // 5 minute timeout
        }
      );
      
      const previewData = response.data;
      setMigrationInfo(previewData);
      setPreviewDialogOpen(true);
      
    } catch (error) {
      console.error('Preview error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Preview Failed',
        html: `
          <p>Could not load preview from Cloud Function.</p>
          <p style="color: #aaa; font-size: 0.9rem; margin-top: 10px;">
            <strong>Alternative:</strong> Run the preview scripts in your terminal:<br>
            <code style="background: #2a2a2a; padding: 5px; display: inline-block; margin-top: 5px;">
              node scripts/migration/migrate-all-collections.js
            </code>
          </p>
        `,
        width: '600px'
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const addLog = (message, type = 'info') => {
    setExecutionLogs(prev => [...prev, { 
      timestamp: new Date().toLocaleTimeString(), 
      message, 
      type 
    }]);
  };

  const handleExecute = () => {
    console.log('🚀 handleExecute called - opening confirmation dialog');
    setConfirmDialogOpen(true);
  };

  const handleConfirmExecute = async () => {
    console.log('User confirmed, starting execution...');
    setConfirmDialogOpen(false);
    // Start execution
    setIsExecuting(true);
    isExecutingRef.current = true;
    setExecutionProgress(0);
    setExecutionLogs([]);
    setCanCancel(true);
    setCurrentStep('Initializing migration...');

    const selectedCols = Object.keys(selectedCollections).filter(
      (key) => selectedCollections[key]
    );

    try {
      addLog('🚀 Migration started', 'success');
      addLog(`📋 Collections to migrate: ${selectedCols.join(', ')}`, 'info');
      addLog(`📅 Date range: ${startDate.format("DD/MM/YYYY")} to ${endDate.format("DD/MM/YYYY")}`, 'info');
      addLog('📡 Calling Cloud Function...', 'info');

      setCurrentStep('Executing migration...');
      setExecutionProgress(10);

      // Call Cloud Function to execute migration
      const response = await axios.post(
        FUNCTIONS_URLS.ExecuteProductionMigration,
        {
          collections: selectedCols,
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
          updateBalance: updateBalance,
          syncTransactionStatus: syncTransactionStatus,
          importPerformance: importPerformance
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 540000 // 9 minutes timeout
        }
      );

      if (!isExecutingRef.current) {
        addLog('❌ Migration cancelled by user', 'error');
        return;
      }

      setExecutionProgress(50);
      addLog('📥 Processing migration results...', 'info');

      if (response.data && response.data.success) {
        const results = response.data.results;
        
        // Log results for each collection
        Object.keys(results.collections).forEach((col, idx) => {
          const result = results.collections[col];
          if (result.migrated > 0 || result.errors > 0) {
            addLog(`\n📦 ${col}:`, 'info');
            addLog(`   ✅ Migrated: ${result.migrated}`, 'success');
            addLog(`   ⏭️  Skipped: ${result.skipped}`, 'info');
            if (result.errors > 0) {
              addLog(`   ❌ Errors: ${result.errors}`, 'error');
            }
          }
        });

        setExecutionProgress(100);
        setCurrentStep('Migration completed successfully!');
        addLog('\n🎉 Migration completed successfully!', 'success');
        setCanCancel(false);
        
        // Show success message
        Swal.fire({
          icon: 'success',
          title: 'Migration Complete!',
          html: `
            <p>All selected collections have been migrated successfully.</p>
            <p><strong>Results:</strong></p>
            <ul style="text-align: left;">
              ${Object.keys(results.collections).map(col => {
                const r = results.collections[col];
                return `<li>${col}: ${r.migrated} migrated, ${r.skipped} skipped, ${r.errors} errors</li>`;
              }).join('')}
            </ul>
          `,
          confirmButtonColor: '#2af598'
        });
      } else {
        throw new Error(response.data?.error || 'Migration failed');
      }
    } catch (error) {
      console.error('Migration error:', error);
      addLog(`❌ Error: ${error.message}`, 'error');
      setCurrentStep('Migration failed');
      
      Swal.fire({
        icon: 'error',
        title: 'Migration Failed',
        text: error.response?.data?.details || error.message || 'An error occurred during migration.',
        confirmButtonColor: '#f52a2d'
      });
    } finally {
      setIsExecuting(false);
      isExecutingRef.current = false;
    }
  };

  const handleCancel = () => {
    setIsExecuting(false);
    isExecutingRef.current = false;
    setCanCancel(false);
    addLog('⚠️ Migration cancelled by user', 'warning');
    setCurrentStep('Migration cancelled');
  };

  const collectionLabels = {
    users: "Users",
    ledgers: "Ledgers",
    monthly_grpahs: "Monthly Graphs",
    monthly_closings: "Monthly Closings",
    transection_requests: "Transaction Requests",
    yearly_graph: "Yearly Graph",
    notifications: "Notifications",
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <CloudUploadIcon sx={{ fontSize: 40, color: "#2af598", mr: 2 }} />
          <Box>
            <Typography variant="h5" sx={{ color: "#fff", fontWeight: "bold" }}>
              Production Migration Tool
            </Typography>
            <Typography variant="body2" sx={{ color: "#aaa" }}>
              Migrate data from production (fiddux-5a1f1) to new database (fidduxai-rowy) with visual preview
            </Typography>
          </Box>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3, backgroundColor: "rgba(42, 245, 152, 0.1)", color: "#2af598" }}>
        <Typography variant="body2">
          <strong>What this tool does:</strong>
        </Typography>
        <Typography variant="body2" component="div" sx={{ mt: 1 }}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>Imports selected collections from production within a date range</li>
            <li>Prevents duplicates by checking existing data before importing</li>
            <li>Optionally syncs transaction status (Pending → Paid)</li>
            <li>Optionally updates user balances after migration</li>
            <li>Allows filtering by specific users or all users</li>
          </ul>
        </Typography>
        <Typography variant="body2" component="div" sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(42, 245, 152, 0.2)" }}>
          <strong>User Import Filters (automatically applied):</strong>
          <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
            <li>✅ Skips blocked users (status = 2)</li>
            <li>✅ Skips admin users (role = 0)</li>
            <li>✅ Skips EquitySoft users (@equitysoft.in email)</li>
            <li>✅ Only imports active users (status = 1)</li>
          </ul>
        </Typography>
      </Alert>

      {/* Configuration Section */}
      <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(42, 245, 152, 0.2)", mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: "#fff", mb: 3 }}>
            Migration Configuration
          </Typography>

          <Grid container spacing={3}>
            {/* Date Range */}
            <Grid item xs={12} md={6}>
              <Card sx={{ background: "rgba(42, 245, 152, 0.05)", border: "1px solid rgba(42, 245, 152, 0.2)", p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <DateRangeIcon sx={{ color: "#2af598", mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: "bold" }}>
                    Date Range
                  </Typography>
                </Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          mb: 2,
                          "& .MuiOutlinedInput-root": {
                            color: "#fff",
                            "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                            "&:hover fieldset": { borderColor: "#2af598" },
                            "&.Mui-focused fieldset": { borderColor: "#2af598" },
                          },
                          "& .MuiInputLabel-root": { color: "#aaa" },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            color: "#fff",
                            "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                            "&:hover fieldset": { borderColor: "#2af598" },
                            "&.Mui-focused fieldset": { borderColor: "#2af598" },
                          },
                          "& .MuiInputLabel-root": { color: "#aaa" },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Card>
            </Grid>

            {/* User Selection */}
            <Grid item xs={12} md={6}>
              <Card sx={{ background: "rgba(42, 245, 152, 0.05)", border: "1px solid rgba(42, 245, 152, 0.2)", p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <PeopleIcon sx={{ color: "#2af598", mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: "bold" }}>
                    User Selection
                  </Typography>
                </Box>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Select
                    value={userSelectionMode}
                    onChange={(e) => setUserSelectionMode(e.target.value)}
                    sx={{
                      color: "#fff",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#2af598",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#2af598",
                      },
                    }}
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    <MenuItem value="specific">Specific Users</MenuItem>
                  </Select>
                </FormControl>
                {userSelectionMode === "specific" && (
                  <Autocomplete
                    multiple
                    options={availableUsers}
                    getOptionLabel={(option) => `${option.full_name} (${option.email})`}
                    value={selectedUsers}
                    onChange={(event, newValue) => setSelectedUsers(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Users"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            color: "#fff",
                            "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                            "&:hover fieldset": { borderColor: "#2af598" },
                            "&.Mui-focused fieldset": { borderColor: "#2af598" },
                          },
                          "& .MuiInputLabel-root": { color: "#aaa" },
                        }}
                      />
                    )}
                  />
                )}
              </Card>
            </Grid>

            {/* Collections Selection */}
            <Grid item xs={12} md={6}>
              <Card sx={{ background: "rgba(42, 245, 152, 0.05)", border: "1px solid rgba(42, 245, 152, 0.2)", p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <StorageIcon sx={{ color: "#2af598", mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: "bold" }}>
                    Collections to Import
                  </Typography>
                </Box>
                <FormGroup>
                  {Object.keys(selectedCollections).map((key) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={selectedCollections[key]}
                          onChange={(e) =>
                            setSelectedCollections({
                              ...selectedCollections,
                              [key]: e.target.checked,
                            })
                          }
                          sx={{
                            color: "#2af598",
                            "&.Mui-checked": { color: "#2af598" },
                          }}
                        />
                      }
                      label={collectionLabels[key]}
                      sx={{ color: "#aaa" }}
                    />
                  ))}
                </FormGroup>
              </Card>
            </Grid>

            {/* Options */}
            <Grid item xs={12} md={6}>
              <Card sx={{ background: "rgba(42, 245, 152, 0.05)", border: "1px solid rgba(42, 245, 152, 0.2)", p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <AccountBalanceIcon sx={{ color: "#2af598", mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: "bold" }}>
                    Options
                  </Typography>
                </Box>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={updateBalance}
                        onChange={(e) => setUpdateBalance(e.target.checked)}
                        sx={{
                          color: "#2af598",
                          "&.Mui-checked": { color: "#2af598" },
                        }}
                      />
                    }
                    label="Update User Balances"
                    sx={{ color: "#aaa" }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={syncTransactionStatus}
                        onChange={(e) => setSyncTransactionStatus(e.target.checked)}
                        disabled={!selectedCollections.transection_requests}
                        sx={{
                          color: "#2af598",
                          "&.Mui-checked": { color: "#2af598" },
                        }}
                      />
                    }
                    label="Sync Transaction Status (Pending → Paid)"
                    sx={{ color: "#aaa" }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={importPerformance}
                        onChange={(e) => setImportPerformance(e.target.checked)}
                        disabled={!selectedCollections.transection_requests}
                        sx={{
                          color: "#2af598",
                          "&.Mui-checked": { color: "#2af598" },
                        }}
                      />
                    }
                    label="Import Performance Transactions (request_type = 2)"
                    sx={{ color: "#aaa" }}
                  />
                  {importPerformance && (
                    <Alert severity="warning" sx={{ mt: 2, backgroundColor: "rgba(255, 152, 0, 0.1)", color: "#ff9800" }}>
                      <Typography variant="caption">
                        ⚠️ <strong>Warning:</strong> Importing Performance transactions from closed months. 
                        These are normally created when closing a month in the new system.
                        Only import if you need historical performance data from production.
                      </Typography>
                    </Alert>
                  )}
                </FormGroup>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mb: 3 }}>
        <Button
          variant="contained"
          startIcon={previewLoading ? <CircularProgress size={20} /> : <VisibilityIcon />}
          onClick={handlePreview}
          disabled={previewLoading || loading || isExecuting}
          sx={{
            background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
            "&:hover": {
              background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
              opacity: 0.9,
            },
          }}
        >
          Preview Migration
        </Button>
      </Box>

      {/* Execution Progress */}
      {loading && (
        <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(42, 245, 152, 0.2)", mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: "#fff", mb: 2 }}>
              Migration Progress
            </Typography>
            <LinearProgress variant="determinate" value={executionProgress} sx={{ mb: 2, height: 8, borderRadius: 4 }} />
            <Typography variant="body2" sx={{ color: "#aaa" }}>
              {executionProgress.toFixed(0)}% Complete
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => {
          if (!isExecuting) {
            setPreviewDialogOpen(false);
            setExecutionLogs([]);
            setExecutionProgress(0);
          }
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
            color: "#fff",
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff", borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>
          Migration Preview
        </DialogTitle>
        <DialogContent>
          {migrationInfo && (
            <Box>
              {/* EXPLANATION: Why Rowy will have more files - MOVED TO TOP */}
              {(() => {
                const collectionsWithMore = Object.keys(selectedCollections)
                  .filter((key) => selectedCollections[key])
                  .map((key) => {
                    const data = migrationInfo.collections?.[key] || {};
                    const prodAll = data.productionAll || 0;
                    const rowyAll = data.rowyAll || data.rowy || 0;
                    const toMigrate = data.toMigrate || 0;
                    const totalFinalRowy = rowyAll + toMigrate;
                    const diff = totalFinalRowy - prodAll;
                    
                    if (diff > 0) {
                      return {
                        key,
                        name: collectionLabels[key],
                        prodAll,
                        rowyAll,
                        toMigrate,
                        totalFinalRowy,
                        diff,
                        rowyOnly: data.rowyOnlyInRange || 0
                      };
                    }
                    return null;
                  })
                  .filter(Boolean);
                
                if (collectionsWithMore.length > 0) {
                  return (
                    <Accordion sx={{ mb: 2, backgroundColor: "rgba(245, 42, 45, 0.1)", border: "2px solid #f52a2d" }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#f52a2d" }} />}>
                        <Typography variant="h6" sx={{ color: "#f52a2d", fontSize: "1.2rem", fontWeight: "bold" }}>
                          🔴 {collectionsWithMore.length} Coleção(ões) com MAIS arquivos no Rowy que Production
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {collectionsWithMore.map((item) => (
                          <Accordion key={item.key} sx={{ mb: 2, backgroundColor: "rgba(0, 0, 0, 0.2)" }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#f52a2d" }} />}>
                              <Typography variant="body1" sx={{ color: "#f52a2d", fontWeight: "bold" }}>
                                📊 {item.name}: +{item.diff} arquivos extras (Total Final: {item.totalFinalRowy} vs Production: {item.prodAll})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box component="ul" sx={{ color: "#fff", fontSize: "1rem", pl: 3, mb: 1.5, lineHeight: 1.8 }}>
                                <li><strong>Production Total:</strong> {item.prodAll} arquivos</li>
                                <li><strong>Rowy Total (atual):</strong> {item.rowyAll} arquivos</li>
                                <li><strong>To Migrate (do range):</strong> {item.toMigrate} arquivos</li>
                                <li><strong>Total Final Rowy:</strong> {item.rowyAll} + {item.toMigrate} = <strong style={{color: "#f52a2d"}}>{item.totalFinalRowy}</strong></li>
                                <li><strong style={{color: "#f52a2d"}}>Diferença: +{item.diff} arquivos A MAIS no Rowy!</strong></li>
                                {item.rowyOnly > 0 && (
                                  <li><strong style={{color: "#2af598"}}>Rowy-only no range: {item.rowyOnly} arquivos</strong></li>
                                )}
                              </Box>
                              <Typography variant="body2" sx={{ color: "#ff9800", fontSize: "0.95rem", fontWeight: "bold", mb: 1 }}>
                                ⚠️ Verifique os documentos abaixo para identificar duplicatas e test data.
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  );
                }
                return null;
              })()}
              
              <Alert severity="info" sx={{ mb: 2, backgroundColor: "rgba(42, 245, 152, 0.1)", color: "#2af598" }}>
                <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                  <strong>Date Range:</strong> {startDate.format("DD/MM/YYYY")} to {endDate.format("DD/MM/YYYY")}
                </Typography>
              </Alert>
              
              {/* Discrepancy Summary - Most Important Section */}
              {(() => {
                const totalRowyOnly = Object.keys(selectedCollections)
                  .filter((key) => selectedCollections[key])
                  .reduce((sum, key) => {
                    const data = migrationInfo.collections?.[key];
                    return sum + (data?.rowyOnlyInRange || 0);
                  }, 0);
                
                const collectionsWithDiscrepancy = Object.keys(selectedCollections)
                  .filter((key) => selectedCollections[key])
                  .filter((key) => {
                    const data = migrationInfo.collections?.[key];
                    const prodFiltered = data?.production || 0;
                    const rowyFiltered = data?.rowyFiltered || 0;
                    return rowyFiltered > prodFiltered;
                  });
                
                if (totalRowyOnly > 0 || collectionsWithDiscrepancy.length > 0) {
                  return (
                    <Alert severity="warning" sx={{ mb: 3, backgroundColor: "rgba(255, 152, 0, 0.15)", border: "2px solid #ff9800" }}>
                      <Typography variant="h6" sx={{ color: "#ff9800", mb: 2, fontSize: "1.2rem", fontWeight: "bold" }}>
                        ⚠️ DEFASAGEM DETECTADA: Rowy tem mais dados que Production
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#fff", mb: 2, fontSize: "1.05rem" }}>
                        <strong>Por que Rowy tem mais dados?</strong>
                      </Typography>
                      <Box component="ul" sx={{ color: "#fff", fontSize: "1rem", pl: 3, mb: 2 }}>
                        <li>Documentos criados durante testes do sistema novo (após 06/01/2026)</li>
                        <li>Documentos criados diretamente no Rowy pelo novo sistema</li>
                        <li>Possíveis duplicatas ou dados de teste que não deveriam estar lá</li>
                      </Box>
                      <Typography variant="body1" sx={{ color: "#fff", mb: 2, fontSize: "1.05rem" }}>
                        <strong>Total de documentos Rowy-only (extras): {totalRowyOnly}</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#fff", mb: 2, fontSize: "1.05rem" }}>
                        <strong>✅ Após migração completa, Production e Rowy devem ter o MESMO número de dados (dentro do range).</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#2af598", fontSize: "1.05rem", fontWeight: "bold" }}>
                        👇 Role para baixo para VER e DELETAR os documentos extras do Rowy
                      </Typography>
                    </Alert>
                  );
                }
                return null;
              })()}
              
              <Alert severity="info" sx={{ mb: 3, backgroundColor: "rgba(42, 245, 152, 0.1)", color: "#2af598" }}>
                <Typography variant="body2" sx={{ fontSize: "1rem", fontWeight: "bold", mb: 1 }}>
                  📊 Explicação das Colunas:
                </Typography>
                <Box component="ul" sx={{ color: "#2af598", fontSize: "0.95rem", pl: 3, mb: 1 }}>
                  <li><strong>Production All:</strong> Total de documentos em Production (sem filtros)</li>
                  <li><strong>Production (Filtered):</strong> Documentos em Production dentro do range de data. Para Users, não há filtro de data, então mostra todos.</li>
                  <li><strong>Rowy (All):</strong> Total de documentos no Rowy (sem filtros)</li>
                  <li><strong>Rowy (Filtered):</strong> Documentos no Rowy dentro do mesmo range. Para Users, não há filtro de data, então mostra todos.</li>
                  <li><strong>To Migrate:</strong> Documentos que serão importados (Production Filtered - Already Exist)</li>
                  <li><strong>Already Exist:</strong> Documentos que já existem no Rowy (mesmo ID do Production)</li>
                  <li><strong>Rowy-only:</strong> Documentos que existem no Rowy (no range) mas NÃO no Production (no range) - provavelmente criados durante testes</li>
                  <li><strong>Filtered:</strong> Para Users = excluídos (bloqueados/admin/equitysoft/inativos). Para Transactions = Performance transactions excluídas.</li>
                </Box>
                {!importPerformance && (
                  <Typography variant="body2" sx={{ fontSize: "0.95rem", mt: 1 }}>
                    <strong>⚠️ Performance Transactions Excluded:</strong> Transaction requests com <code>request_type = 2</code> são automaticamente excluídas.
                  </Typography>
                )}
                <Typography variant="body2" sx={{ fontSize: "0.95rem", mt: 1 }}>
                  <strong>🛡️ Duplicate Protection:</strong> O tool automaticamente pula documentos que já existem no Rowy (verificado por document ID).
                </Typography>
              </Alert>
              
              <TableContainer component={Paper} sx={{ backgroundColor: "rgba(0, 0, 0, 0.3)", mb: 2, fontSize: "1.1rem" }}>
                <Table size="medium" sx={{ fontSize: "1.1rem" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem", py: 2 }}>Collection</TableCell>
                      <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem", py: 2 }}>
                        <Tooltip 
                          title="Total de documentos em Production (sem filtros de data)"
                          componentsProps={{
                            tooltip: {
                              sx: { fontSize: "1rem", backgroundColor: "rgba(0, 0, 0, 0.9)", padding: "12px" }
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            Production All
                            <InfoIcon sx={{ fontSize: 18, cursor: "help" }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem", py: 2 }}>
                        <Tooltip 
                          title="Documentos em Production dentro do range de data selecionado. Para Users, não há filtro de data, então mostra todos."
                          componentsProps={{
                            tooltip: {
                              sx: { fontSize: "1rem", backgroundColor: "rgba(0, 0, 0, 0.9)", padding: "12px", maxWidth: "400px" }
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            Production (Filtered)
                            <InfoIcon sx={{ fontSize: 18, cursor: "help" }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem", py: 2 }}>
                        <Tooltip 
                          title="Total de documentos no Rowy (sem filtros de data)"
                          componentsProps={{
                            tooltip: {
                              sx: { fontSize: "1rem", backgroundColor: "rgba(0, 0, 0, 0.9)", padding: "12px" }
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            Rowy (All)
                            <InfoIcon sx={{ fontSize: 18, cursor: "help" }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem", py: 2 }}>
                        <Tooltip 
                          title="Documentos no Rowy dentro do mesmo range de data do Production. Para Users, não há filtro de data, então mostra todos."
                          componentsProps={{
                            tooltip: {
                              sx: { fontSize: "1rem", backgroundColor: "rgba(0, 0, 0, 0.9)", padding: "12px", maxWidth: "400px" }
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            Rowy (Filtered)
                            <InfoIcon sx={{ fontSize: 18, cursor: "help" }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem", py: 2 }}>
                        <Tooltip 
                          title="Documentos que serão importados do Production para o Rowy (Production Filtered - Already Exist)"
                          componentsProps={{
                            tooltip: {
                              sx: { fontSize: "1rem", backgroundColor: "rgba(0, 0, 0, 0.9)", padding: "12px", maxWidth: "400px" }
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            To Migrate
                            <InfoIcon sx={{ fontSize: 18, cursor: "help" }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem", py: 2 }}>
                        <Tooltip 
                          title="Documentos que já existem no Rowy (mesmo ID do Production)"
                          componentsProps={{
                            tooltip: {
                              sx: { fontSize: "1rem", backgroundColor: "rgba(0, 0, 0, 0.9)", padding: "12px" }
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            Already Exist
                            <InfoIcon sx={{ fontSize: 18, cursor: "help" }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem", py: 2 }}>
                        <Tooltip 
                          title="Documentos que existem no Rowy (no range) mas NÃO existem no Production (no range). Provavelmente criados durante testes."
                          componentsProps={{
                            tooltip: {
                              sx: { fontSize: "1rem", backgroundColor: "rgba(0, 0, 0, 0.9)", padding: "12px", maxWidth: "400px" }
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            Rowy-only
                            <InfoIcon sx={{ fontSize: 18, cursor: "help" }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem", py: 2 }}>
                        <Tooltip 
                          title="Para Users: usuários excluídos (bloqueados/admin/equitysoft/inativos). Para Transactions: Performance transactions excluídas (request_type=2)."
                          componentsProps={{
                            tooltip: {
                              sx: { fontSize: "1rem", backgroundColor: "rgba(0, 0, 0, 0.9)", padding: "12px", maxWidth: "400px" }
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            Filtered
                            <InfoIcon sx={{ fontSize: 18, cursor: "help" }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem", py: 2, backgroundColor: "rgba(42, 245, 152, 0.2)" }}>
                        <Tooltip 
                          title="Total final em Production após migração (Production Filtered - não muda, só leitura)"
                          componentsProps={{
                            tooltip: {
                              sx: { fontSize: "1rem", backgroundColor: "rgba(0, 0, 0, 0.9)", padding: "12px", maxWidth: "400px" }
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            Total Final Production
                            <InfoIcon sx={{ fontSize: 18, cursor: "help" }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem", py: 2, backgroundColor: "rgba(42, 245, 152, 0.2)" }}>
                        <Tooltip 
                          title="Total final em Rowy após migração (Rowy Filtered + To Migrate). Se Rowy-only for deletado, será menor."
                          componentsProps={{
                            tooltip: {
                              sx: { fontSize: "1rem", backgroundColor: "rgba(0, 0, 0, 0.9)", padding: "12px", maxWidth: "400px" }
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            Total Final Rowy
                            <InfoIcon sx={{ fontSize: 18, cursor: "help" }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem", py: 2, backgroundColor: "rgba(255, 152, 0, 0.2)" }}>
                        <Tooltip 
                          title="Diferença final: Total Final Rowy - Total Final Production. Se positivo, Rowy terá mais. Se zero, estarão iguais (ideal)."
                          componentsProps={{
                            tooltip: {
                              sx: { fontSize: "1rem", backgroundColor: "rgba(0, 0, 0, 0.9)", padding: "12px", maxWidth: "400px" }
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            Diferença Final
                            <InfoIcon sx={{ fontSize: 18, cursor: "help" }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(selectedCollections)
                      .filter((key) => selectedCollections[key])
                      .map((key) => {
                        const data = migrationInfo.collections?.[key] || {};
                        const isDateFiltered = data.dateFiltered;
                        return (
                          <TableRow key={key} sx={{ '& td': { fontSize: "1.05rem", py: 1.5 } }}>
                            <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>{collectionLabels[key]}</TableCell>
                            <TableCell align="right" sx={{ color: "#aaa", fontWeight: "bold" }}>
                              {data.productionAll ?? "N/A"}
                            </TableCell>
                            <TableCell align="right" sx={{ color: "#ff9800", fontWeight: "bold" }}>
                              {data.production ?? "N/A"}
                              {isDateFiltered && key !== "users" && (
                                <Typography variant="caption" sx={{ display: "block", color: "#aaa", fontSize: "0.85rem" }}>
                                  (no range)
                                </Typography>
                              )}
                              {key === "users" && (
                                <Typography variant="caption" sx={{ display: "block", color: "#aaa", fontSize: "0.85rem" }}>
                                  (sem filtro de data)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ color: "#2af598", fontWeight: "bold" }}>
                              {data.rowy ?? 0}
                            </TableCell>
                            <TableCell align="right" sx={{ color: "#2af598", fontWeight: "bold" }}>
                              {data.rowyFiltered ?? "N/A"}
                              {key === "users" && (
                                <Typography variant="caption" sx={{ display: "block", color: "#aaa", fontSize: "0.85rem" }}>
                                  (sem filtro de data)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ color: "#2af598", fontWeight: "bold" }}>
                              {data.toMigrate ?? 0}
                            </TableCell>
                            <TableCell align="right" sx={{ color: "#aaa" }}>
                              {data.alreadyExist ?? 0}
                            </TableCell>
                            <TableCell align="right" sx={{ 
                              color: (data.rowyOnlyInRange ?? 0) > 0 ? "#f52a2d" : "#2af598",
                              fontWeight: "bold"
                            }}>
                              {data.rowyOnlyInRange ?? 0}
                              {(data.rowyOnlyInRange ?? 0) > 0 && data.rowyOnlySampleIds && data.rowyOnlySampleIds.length > 0 && (
                                <Tooltip title={`Sample IDs: ${data.rowyOnlySampleIds.slice(0, 3).join(', ')}`}>
                                  <InfoIcon sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'middle', cursor: 'help' }} />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ color: (key === "users" || key === "transection_requests") && data.filtered > 0 ? "#ff9800" : "#aaa" }}>
                              {key === "users" ? (data.filtered ?? 0) : key === "transection_requests" ? (data.filtered ?? 0) : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ 
                              color: "#aaa",
                              fontWeight: "bold",
                              fontSize: "1.1rem"
                            }}>
                              {data.production ?? "N/A"}
                              <Typography variant="caption" sx={{ display: "block", color: "#666", fontSize: "0.8rem" }}>
                                (não muda)
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ 
                              color: (() => {
                                const rowyFiltered = data.rowyFiltered || 0;
                                const toMigrate = data.toMigrate || 0;
                                const totalFinal = rowyFiltered + toMigrate;
                                return "#2af598";
                              })(),
                              fontWeight: "bold",
                              fontSize: "1.1rem"
                            }}>
                              {(() => {
                                // CORRETO: Rowy All + To Migrate
                                const rowyAll = data.rowyAll || data.rowy || 0;
                                const toMigrate = data.toMigrate || 0;
                                const totalFinalRowy = rowyAll + toMigrate;
                                return totalFinalRowy;
                              })()}
                              <Typography variant="caption" sx={{ display: "block", color: "#666", fontSize: "0.8rem" }}>
                                ({data.rowyAll || data.rowy || 0} Rowy All + {data.toMigrate || 0} To Migrate)
                              </Typography>
                              {(data.rowyOnlyInRange || 0) > 0 && (
                                <Typography variant="caption" sx={{ display: "block", color: "#f52a2d", fontSize: "0.8rem", fontWeight: "bold" }}>
                                  Se deletar {data.rowyOnlyInRange} Rowy-only: {(() => {
                                    const rowyAll = data.rowyAll || data.rowy || 0;
                                    const toMigrate = data.toMigrate || 0;
                                    const rowyOnly = data.rowyOnlyInRange || 0;
                                    return (rowyAll + toMigrate - rowyOnly);
                                  })()}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ 
                              color: (() => {
                                const prodAll = data.productionAll || 0;
                                const rowyAll = data.rowyAll || data.rowy || 0;
                                const toMigrate = data.toMigrate || 0;
                                const totalFinalRowy = rowyAll + toMigrate;
                                const diff = totalFinalRowy - prodAll;
                                if (diff > 0) return "#f52a2d"; // Rowy terá mais - PROBLEMA
                                if (diff < 0) return "#ff9800"; // Production terá mais
                                return "#2af598"; // Iguais - ideal
                              })(),
                              fontWeight: "bold",
                              fontSize: "1.2rem",
                              backgroundColor: (() => {
                                const prodAll = data.productionAll || 0;
                                const rowyAll = data.rowyAll || data.rowy || 0;
                                const toMigrate = data.toMigrate || 0;
                                const totalFinalRowy = rowyAll + toMigrate;
                                const diff = totalFinalRowy - prodAll;
                                if (diff > 0) return "rgba(245, 42, 45, 0.3)"; // Rowy terá mais - PROBLEMA
                                if (diff < 0) return "rgba(255, 152, 0, 0.2)"; // Production terá mais
                                return "rgba(42, 245, 152, 0.1)"; // Iguais
                              })()
                            }}>
                              {(() => {
                                const prodAll = data.productionAll || 0;
                                const rowyAll = data.rowyAll || data.rowy || 0;
                                const toMigrate = data.toMigrate || 0;
                                const totalFinalRowy = rowyAll + toMigrate;
                                const diff = totalFinalRowy - prodAll;
                                
                                if (diff > 0) {
                                  return `+${diff} (Rowy terá MAIS!)`;
                                } else if (diff < 0) {
                                  return `${diff} (Production terá mais)`;
                                } else {
                                  return "0 (IGUAIS ✅)";
                                }
                              })()}
                              {(() => {
                                const prodAll = data.productionAll || 0;
                                const rowyAll = data.rowyAll || data.rowy || 0;
                                const toMigrate = data.toMigrate || 0;
                                const totalFinalRowy = rowyAll + toMigrate;
                                const diff = totalFinalRowy - prodAll;
                                
                                if (diff > 0) {
                                  return (
                                    <Typography variant="caption" sx={{ display: "block", color: "#f52a2d", fontSize: "0.9rem", fontWeight: "bold", mt: 0.5 }}>
                                      ⚠️ PROBLEMA: Rowy terá {diff} arquivos A MAIS que Production!
                                    </Typography>
                                  );
                                }
                                return null;
                              })()}
                              {(data.rowyOnlyInRange || 0) > 0 && (
                                <Typography variant="caption" sx={{ display: "block", color: "#2af598", fontSize: "0.85rem", mt: 0.5 }}>
                                  Se deletar {data.rowyOnlyInRange} Rowy-only: {(() => {
                                    const prodAll = data.productionAll || 0;
                                    const rowyAll = data.rowyAll || data.rowy || 0;
                                    const toMigrate = data.toMigrate || 0;
                                    const rowyOnly = data.rowyOnlyInRange || 0;
                                    const totalFinalRowyAfterDelete = rowyAll + toMigrate - rowyOnly;
                                    const diffAfterDelete = totalFinalRowyAfterDelete - prodAll;
                                    if (diffAfterDelete === 0) return "✅ 0 (perfeito!)";
                                    if (diffAfterDelete > 0) return `+${diffAfterDelete} (ainda mais)`;
                                    return `${diffAfterDelete} (Production terá mais)`;
                                  })()}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Summary of Totals After Migration */}
              <Alert severity="info" sx={{ mb: 3, backgroundColor: "rgba(42, 245, 152, 0.15)", border: "2px solid #2af598" }}>
                <Typography variant="h6" sx={{ color: "#2af598", mb: 2, fontSize: "1.2rem", fontWeight: "bold" }}>
                  📊 RESUMO: Totais Finais Após Migração
                </Typography>
                <TableContainer component={Paper} sx={{ backgroundColor: "rgba(0, 0, 0, 0.3)", mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem" }}>Collection</TableCell>
                        <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem" }}>Total Final Production</TableCell>
                        <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem" }}>Total Final Rowy (se deletar Rowy-only)</TableCell>
                        <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem" }}>Diferença Final</TableCell>
                        <TableCell align="right" sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem" }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.keys(selectedCollections)
                        .filter((key) => selectedCollections[key])
                        .map((key) => {
                          const data = migrationInfo.collections?.[key] || {};
                          // Use TOTAL counts, not filtered!
                          const totalFinalProduction = data.productionAll || 0;
                          const rowyAll = data.rowyAll || data.rowy || 0;
                          const toMigrate = data.toMigrate || 0;
                          const totalFinalRowy = rowyAll + toMigrate;
                          const rowyOnly = data.rowyOnlyInRange || 0;
                          const totalFinalRowyAfterDelete = totalFinalRowy - rowyOnly;
                          const diffAfterDelete = totalFinalRowyAfterDelete - totalFinalProduction;
                          const diffBeforeDelete = totalFinalRowy - totalFinalProduction;
                          const hasInconsistency = diffBeforeDelete > 5 || diffAfterDelete > 5;
                          
                          return (
                            <TableRow key={key} sx={{ 
                              backgroundColor: hasInconsistency ? "rgba(245, 42, 45, 0.1)" : "transparent"
                            }}>
                              <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.05rem" }}>{collectionLabels[key]}</TableCell>
                              <TableCell align="right" sx={{ color: "#aaa", fontSize: "1.05rem", fontWeight: "bold" }}>
                                {totalFinalProduction}
                                <Typography variant="caption" sx={{ display: "block", color: "#666", fontSize: "0.75rem" }}>
                                  (Production Total)
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ color: "#2af598", fontSize: "1.05rem", fontWeight: "bold" }}>
                                {totalFinalRowyAfterDelete}
                                <Typography variant="caption" sx={{ display: "block", color: "#666", fontSize: "0.75rem" }}>
                                  ({rowyAll} Rowy All + {toMigrate} To Migrate - {rowyOnly} Rowy-only)
                                </Typography>
                                {rowyOnly > 0 && (
                                  <Typography variant="caption" sx={{ display: "block", color: "#f52a2d", fontSize: "0.85rem", mt: 0.5, fontWeight: "bold" }}>
                                    ANTES de deletar Rowy-only: {totalFinalRowy} (diferença: +{diffBeforeDelete})
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ 
                                color: diffAfterDelete === 0 ? "#2af598" : diffAfterDelete > 0 ? "#f52a2d" : "#ff9800",
                                fontSize: "1.2rem",
                                fontWeight: "bold"
                              }}>
                                {diffAfterDelete === 0 ? "✅ 0 (PERFEITO!)" : diffAfterDelete > 0 ? `+${diffAfterDelete} (Rowy terá MAIS!)` : `${diffAfterDelete} (Production terá mais)`}
                                {diffAfterDelete > 0 && (
                                  <Typography variant="caption" sx={{ display: "block", color: "#f52a2d", fontSize: "0.9rem", fontWeight: "bold", mt: 0.5 }}>
                                    ⚠️ Rowy terá {diffAfterDelete} arquivos A MAIS que Production!
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {diffAfterDelete === 0 ? (
                                  <Chip label="✅ IGUAIS" size="small" sx={{ backgroundColor: "#2af598", color: "#000", fontWeight: "bold" }} />
                                ) : hasInconsistency ? (
                                  <Chip label="🔴 PROBLEMA" size="small" sx={{ backgroundColor: "#f52a2d", color: "#fff", fontWeight: "bold" }} />
                                ) : rowyOnly > 0 ? (
                                  <Chip label="⚠️ DELETAR Rowy-only" size="small" sx={{ backgroundColor: "#ff9800", color: "#fff", fontWeight: "bold" }} />
                                ) : (
                                  <Chip label="⚠️ REVISAR" size="small" sx={{ backgroundColor: "#ff9800", color: "#fff" }} />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="body1" sx={{ color: "#2af598", mt: 2, fontSize: "1.05rem", fontWeight: "bold" }}>
                  ✅ Objetivo: Diferença Final = 0 para todas as coleções (Production Total e Rowy Total devem ser IGUAIS após migração e limpeza)
                </Typography>
                <Alert severity="error" sx={{ mt: 2, backgroundColor: "rgba(245, 42, 45, 0.2)", border: "2px solid #f52a2d" }}>
                  <Typography variant="body1" sx={{ color: "#f52a2d", fontSize: "1.1rem", fontWeight: "bold" }}>
                    ⚠️ ATENÇÃO: Se "Diferença Final" for positiva (Rowy terá mais), há uma INCONSISTÊNCIA!
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#fff", mt: 1, fontSize: "1rem" }}>
                    Isso significa que a lógica de detecção de duplicatas pode estar falhando, ou há documentos na Production que não estão sendo detectados no Rowy.
                    <strong> NÃO migre até resolver essa inconsistência!</strong>
                  </Typography>
                </Alert>
              </Alert>
              
              {/* DISCREPANCY SECTION - Rowy-only Documents */}
              {(() => {
                const hasRowyOnly = Object.keys(selectedCollections)
                  .filter((key) => selectedCollections[key])
                  .some((key) => {
                    const data = migrationInfo.collections?.[key];
                    return (data.rowyOnlyInRange ?? 0) > 0;
                  });
                
                if (!hasRowyOnly) return null;
                
                const totalRowyOnly = Object.keys(selectedCollections)
                  .filter((key) => selectedCollections[key])
                  .reduce((sum, key) => {
                    const data = migrationInfo.collections?.[key];
                    return sum + (data?.rowyOnlyInRange || 0);
                  }, 0);
                
                return (
                  <Box sx={{ mb: 4 }}>
                    <Alert severity="error" sx={{ mb: 3, backgroundColor: "rgba(245, 42, 45, 0.2)", border: "3px solid #f52a2d", p: 3 }}>
                      <Typography variant="h4" sx={{ color: "#f52a2d", mb: 2, fontSize: "1.5rem", fontWeight: "bold" }}>
                        🔴 {totalRowyOnly} DOCUMENTOS EXTRAS NO ROWY - AÇÃO NECESSÁRIA
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#fff", mb: 2, fontSize: "1.15rem", lineHeight: 1.6 }}>
                        <strong>Estes {totalRowyOnly} documentos existem no Rowy mas NÃO existem no Production (dentro do range selecionado).</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#fff", mb: 1, fontSize: "1.15rem", fontWeight: "bold" }}>
                        Por que isso acontece?
                      </Typography>
                      <Box component="ul" sx={{ color: "#fff", fontSize: "1.1rem", pl: 3, mb: 2, lineHeight: 1.8 }}>
                        <li>Foram criados durante testes do sistema novo (após 06/01/2026)</li>
                        <li>Foram criados diretamente no Rowy pelo novo sistema</li>
                        <li>São dados de teste que não deveriam estar em produção</li>
                      </Box>
                      <Typography variant="body1" sx={{ color: "#2af598", fontSize: "1.15rem", fontWeight: "bold", mb: 2 }}>
                        ✅ Após migração completa, Production e Rowy devem ter o MESMO número de dados (dentro do range).
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#ff9800", fontSize: "1.2rem", fontWeight: "bold", mt: 2, p: 2, backgroundColor: "rgba(255, 152, 0, 0.1)", borderRadius: 1 }}>
                        👇 EXPANDA AS SEÇÕES ABAIXO para VER os detalhes de cada documento e DELETAR os que são test data.
                      </Typography>
                    </Alert>
                  </Box>
                );
              })()}
              
              {/* Rowy-only Documents Detailed View */}
              {Object.keys(selectedCollections)
                .filter((key) => selectedCollections[key])
                .filter((key) => {
                  const data = migrationInfo.collections?.[key];
                  return (data?.rowyOnlyInRange ?? 0) > 0;
                })
                .map((key) => {
                  const data = migrationInfo.collections?.[key];
                  if (!data) return null;
                  const rowyOnlyDetails = data.rowyOnlyDetails || [];
                  const hasDetails = rowyOnlyDetails.length > 0;
                  
                  // Group by risk level (only if we have details)
                  const byRisk = hasDetails ? {
                    high: rowyOnlyDetails.filter(d => d.riskLevel === 'high'),
                    medium: rowyOnlyDetails.filter(d => d.riskLevel === 'medium'),
                    low: rowyOnlyDetails.filter(d => d.riskLevel === 'low')
                  } : { high: [], medium: [], low: [] };
                  
                  return (
                    <Accordion
                      key={key}
                      expanded={expandedRowyOnly === key}
                      onChange={() => setExpandedRowyOnly(expandedRowyOnly === key ? null : key)}
                      sx={{
                        backgroundColor: "rgba(255, 152, 0, 0.05)",
                        border: "2px solid rgba(255, 152, 0, 0.5)",
                        mb: 2,
                        "&:before": { display: "none" }
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#ff9800", fontSize: "2rem" }} />}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                          <WarningIcon sx={{ color: "#ff9800", fontSize: "2rem" }} />
                          <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                            {collectionLabels[key]}: {data?.rowyOnlyInRange ?? 0} Documentos Extras no Rowy
                          </Typography>
                          {hasDetails && (
                            <>
                              {byRisk.high.length > 0 && (
                                <Chip label={`${byRisk.high.length} high risk`} size="medium" sx={{ backgroundColor: "#f52a2d", color: "#fff", fontSize: "0.95rem", height: "32px" }} />
                              )}
                              {byRisk.medium.length > 0 && (
                                <Chip label={`${byRisk.medium.length} medium`} size="medium" sx={{ backgroundColor: "#ff9800", color: "#fff", fontSize: "0.95rem", height: "32px" }} />
                              )}
                              {byRisk.low.length > 0 && (
                                <Chip label={`${byRisk.low.length} low`} size="medium" sx={{ backgroundColor: "#2af598", color: "#000", fontSize: "0.95rem", height: "32px" }} />
                              )}
                            </>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {!hasDetails && (
                          <Box>
                            <Alert severity="warning" sx={{ mb: 3, backgroundColor: "rgba(255, 152, 0, 0.15)", border: "2px solid #ff9800" }}>
                              <Typography variant="h6" sx={{ color: "#ff9800", mb: 2, fontSize: "1.2rem", fontWeight: "bold" }}>
                                ⚠️ Detalhes dos documentos não disponíveis na UI
                              </Typography>
                              <Typography variant="body1" sx={{ color: "#fff", mb: 2, fontSize: "1.05rem" }}>
                                Para ver os detalhes completos (user_id, date, amount, risco) e poder deletar via UI, execute o script standalone que gera análise completa:
                              </Typography>
                              <Box sx={{ 
                                backgroundColor: "rgba(0, 0, 0, 0.5)", 
                                p: 2.5, 
                                borderRadius: 1,
                                fontFamily: "monospace",
                                fontSize: "1.1rem",
                                mb: 3,
                                border: "1px solid rgba(42, 245, 152, 0.3)"
                              }}>
                                node scripts/migration/analyze-discrepancies-complete.js {startDate.format('YYYY-MM-DD')} {endDate.format('YYYY-MM-DD')}
                              </Box>
                              <Typography variant="body1" sx={{ color: "#fff", mb: 2, fontSize: "1.05rem", fontWeight: "bold" }}>
                                Ou use os IDs abaixo para verificar manualmente no Firestore Console:
                              </Typography>
                            </Alert>
                            
                            {data.rowyOnlySampleIds && data.rowyOnlySampleIds.length > 0 && (
                              <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" sx={{ color: "#fff", mb: 2, fontSize: "1.2rem", fontWeight: "bold" }}>
                                  📋 IDs dos Documentos Rowy-only ({data.rowyOnlySampleIds.length} de {data.rowyOnlyInRange}):
                                </Typography>
                                <TableContainer component={Paper} sx={{ backgroundColor: "rgba(245, 42, 45, 0.1)", mb: 2 }}>
                                  <Table>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem" }}>#</TableCell>
                                        <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem" }}>Document ID</TableCell>
                                        <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem" }}>Ação</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {data.rowyOnlySampleIds.map((id, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell sx={{ color: "#aaa", fontSize: "1rem" }}>{idx + 1}</TableCell>
                                          <TableCell sx={{ color: "#aaa", fontFamily: "monospace", fontSize: "1rem" }}>
                                            {id}
                                          </TableCell>
                                          <TableCell>
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              onClick={() => {
                                                window.open(`https://console.firebase.google.com/project/fidduxai-rowy/firestore/data/~2F${key}~2F${id}`, '_blank');
                                              }}
                                              sx={{ 
                                                color: "#2af598", 
                                                borderColor: "#2af598",
                                                fontSize: "0.9rem",
                                                "&:hover": {
                                                  borderColor: "#2af598",
                                                  backgroundColor: "rgba(42, 245, 152, 0.1)"
                                                }
                                              }}
                                            >
                                              Ver no Firestore
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                                {data.rowyOnlyInRange > data.rowyOnlySampleIds.length && (
                                  <Alert severity="info" sx={{ backgroundColor: "rgba(42, 245, 152, 0.1)", color: "#2af598" }}>
                                    <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                                      Mostrando apenas {data.rowyOnlySampleIds.length} de {data.rowyOnlyInRange} documentos. 
                                      Execute o script para ver todos os {data.rowyOnlyInRange} documentos com análise completa.
                                    </Typography>
                                  </Alert>
                                )}
                              </Box>
                            )}
                          </Box>
                        )}
                        
                        {hasDetails && (
                          <>
                        {/* COMPARATIVE VIEW: Show ALL fields side by side */}
                        {rowyOnlyDetails.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ color: "#fff", mb: 2, fontSize: "1.2rem", fontWeight: "bold" }}>
                              📋 Comparação Detalhada: {rowyOnlyDetails.length} Documentos Rowy-only
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#aaa", mb: 2, fontSize: "0.95rem" }}>
                              Compare TODOS os campos para identificar duplicatas e decidir o que deletar.
                            </Typography>
                            
                            {rowyOnlyDetails.map((rowyDoc, idx) => {
                              // Find potential duplicates by comparing fields (not just IDs)
                              const potentialDuplicates = rowyOnlyDetails.filter((otherDoc, otherIdx) => {
                                if (idx === otherIdx) return false;
                                // Compare key fields based on collection
                                if (key === 'notifications') {
                                  return (
                                    rowyDoc.allFields?.user_id === otherDoc.allFields?.user_id &&
                                    rowyDoc.allFields?.date === otherDoc.allFields?.date &&
                                    rowyDoc.allFields?.title === otherDoc.allFields?.title &&
                                    rowyDoc.allFields?.body === otherDoc.allFields?.body &&
                                    rowyDoc.allFields?.txn_doc_id === otherDoc.allFields?.txn_doc_id
                                  );
                                }
                                // Add other collection comparisons as needed
                                return false;
                              });
                              
                              const isDuplicate = potentialDuplicates.length > 0;
                              
                              return (
                                <Accordion key={rowyDoc.id} sx={{ mb: 2, backgroundColor: isDuplicate ? "rgba(255, 152, 0, 0.1)" : "rgba(0, 0, 0, 0.2)" }}>
                                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                                      <Checkbox
                                        checked={(selectedRowyOnlyDocs[key] || []).includes(rowyDoc.id)}
                                        onChange={() => handleSelectRowyOnlyDoc(key, rowyDoc.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{ color: "#fff", "&.Mui-checked": { color: "#f52a2d" } }}
                                      />
                                      <Typography variant="body1" sx={{ color: "#fff", fontFamily: "monospace", fontSize: "0.95rem" }}>
                                        {rowyDoc.id}
                                      </Typography>
                                      {isDuplicate && (
                                        <Chip label="⚠️ POSSÍVEL DUPLICATA" size="small" sx={{ backgroundColor: "#ff9800", color: "#fff" }} />
                                      )}
                                      <Chip 
                                        label={rowyDoc.riskLevel.toUpperCase()} 
                                        size="small" 
                                        sx={{ 
                                          backgroundColor: rowyDoc.riskLevel === 'high' ? "#f52a2d" : rowyDoc.riskLevel === 'medium' ? "#ff9800" : "#2af598",
                                          color: "#fff"
                                        }} 
                                      />
                                    </Box>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ color: "#ff9800", mb: 1, fontWeight: "bold" }}>
                                          TODOS OS CAMPOS DO DOCUMENTO ROWY:
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ backgroundColor: "rgba(0, 0, 0, 0.3)", mb: 2 }}>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ color: "#fff", fontWeight: "bold", width: "30%" }}>Campo</TableCell>
                                                <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Valor</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {rowyDoc.allFields && Object.keys(rowyDoc.allFields).map((fieldKey) => (
                                                <TableRow key={fieldKey}>
                                                  <TableCell sx={{ color: "#aaa", fontSize: "0.9rem", fontFamily: "monospace" }}>
                                                    {fieldKey}
                                                  </TableCell>
                                                  <TableCell sx={{ color: "#fff", fontSize: "0.9rem", wordBreak: "break-word" }}>
                                                    {typeof rowyDoc.allFields[fieldKey] === 'object' 
                                                      ? JSON.stringify(rowyDoc.allFields[fieldKey], null, 2)
                                                      : String(rowyDoc.allFields[fieldKey] || 'null')
                                                    }
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </Grid>
                                      
                                      {isDuplicate && (
                                        <Grid item xs={12}>
                                          <Alert severity="warning" sx={{ mb: 2 }}>
                                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                                              ⚠️ Este documento parece ser DUPLICATA de:
                                            </Typography>
                                            {potentialDuplicates.map((dup) => (
                                              <Typography key={dup.id} variant="body2" sx={{ fontFamily: "monospace", ml: 2 }}>
                                                - {dup.id}
                                              </Typography>
                                            ))}
                                            <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                                              Campos idênticos: user_id, date, title, body, txn_doc_id. Apenas IDs diferentes.
                                            </Typography>
                                          </Alert>
                                        </Grid>
                                      )}
                                      
                                      <Grid item xs={12}>
                                        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                              window.open(`https://console.firebase.google.com/project/fidduxai-rowy/firestore/data/~2F${key}~2F${rowyDoc.id}`, '_blank');
                                            }}
                                            sx={{ color: "#2af598", borderColor: "#2af598" }}
                                          >
                                            Ver no Firestore
                                          </Button>
                                          <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => {
                                              setDocToDelete({ collection: key, docIds: [rowyDoc.id] });
                                              setDeleteDialogOpen(true);
                                            }}
                                            sx={{ backgroundColor: "#f52a2d", color: "#fff" }}
                                          >
                                            Deletar Este Documento
                                          </Button>
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </AccordionDetails>
                                </Accordion>
                              );
                            })}
                          </Box>
                        )}
                        
                        {/* OLD High Risk Section - REMOVED */}
                        {false && byRisk.high.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                              <Typography variant="h6" sx={{ color: "#f52a2d" }}>
                                🔴 High Risk ({byRisk.high.length}) - Recommended: DELETE
                              </Typography>
                              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                <Button
                                  size="small"
                                  onClick={() => handleSelectAllInRisk(key, byRisk.high, 'high')}
                                  sx={{ color: "#fff", fontSize: "0.85rem" }}
                                >
                                  {byRisk.high.every(d => (selectedRowyOnlyDocs[key] || []).includes(d.id)) ? 'Deselect All' : 'Select All'}
                                </Button>
                                {getSelectedCount(key) > 0 && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => {
                                      setDocToDelete({ collection: key, docIds: selectedRowyOnlyDocs[key] || [] });
                                      setDeleteDialogOpen(true);
                                    }}
                                    sx={{ backgroundColor: "#f52a2d", color: "#fff" }}
                                  >
                                    Delete Selected ({getSelectedCount(key)})
                                  </Button>
                                )}
                              </Box>
                            </Box>
                            <TableContainer component={Paper} sx={{ backgroundColor: "rgba(245, 42, 45, 0.1)", mb: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell padding="checkbox" sx={{ color: "#fff", fontWeight: "bold" }}>
                                      <Checkbox
                                        checked={byRisk.high.length > 0 && byRisk.high.every(d => (selectedRowyOnlyDocs[key] || []).includes(d.id))}
                                        indeterminate={byRisk.high.some(d => (selectedRowyOnlyDocs[key] || []).includes(d.id)) && !byRisk.high.every(d => (selectedRowyOnlyDocs[key] || []).includes(d.id))}
                                        onChange={() => handleSelectAllInRisk(key, byRisk.high, 'high')}
                                        sx={{ color: "#fff", "&.Mui-checked": { color: "#f52a2d" } }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1rem" }}>ID</TableCell>
                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1rem" }}>User ID</TableCell>
                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1rem" }}>Date</TableCell>
                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1rem" }}>Amount</TableCell>
                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1rem" }}>Reasons</TableCell>
                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1rem" }}>Action</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {byRisk.high.map((doc) => {
                                    const isSelected = (selectedRowyOnlyDocs[key] || []).includes(doc.id);
                                    return (
                                      <TableRow key={doc.id} selected={isSelected}>
                                        <TableCell padding="checkbox">
                                          <Checkbox
                                            checked={isSelected}
                                            onChange={() => handleSelectRowyOnlyDoc(key, doc.id)}
                                            sx={{ color: "#fff", "&.Mui-checked": { color: "#f52a2d" } }}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ color: "#aaa", fontFamily: "monospace", fontSize: "0.9rem" }}>
                                          {doc.id}
                                        </TableCell>
                                        <TableCell sx={{ color: "#aaa", fontSize: "0.95rem" }}>{doc.relevantFields.user_id || 'N/A'}</TableCell>
                                        <TableCell sx={{ color: "#aaa", fontSize: "0.95rem" }}>
                                          {doc.relevantFields.date ? new Date(doc.relevantFields.date).toLocaleDateString() : 'N/A'}
                                        </TableCell>
                                        <TableCell sx={{ color: "#aaa", fontSize: "0.95rem" }}>
                                          {doc.relevantFields.amount ? `$${Number(doc.relevantFields.amount).toFixed(2)}` : 'N/A'}
                                        </TableCell>
                                        <TableCell sx={{ color: "#aaa", fontSize: "0.9rem" }}>
                                          {doc.riskReasons.join(', ')}
                                        </TableCell>
                                        <TableCell>
                                          <Chip label="DELETE" size="small" sx={{ backgroundColor: "#f52a2d", color: "#fff" }} />
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                        
                        {/* Medium Risk Section */}
                        {byRisk.medium.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                              <Typography variant="h6" sx={{ color: "#ff9800" }}>
                                🟡 Medium Risk ({byRisk.medium.length}) - Recommended: REVIEW
                              </Typography>
                              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                <Button
                                  size="small"
                                  onClick={() => handleSelectAllInRisk(key, byRisk.medium, 'medium')}
                                  sx={{ color: "#fff", fontSize: "0.85rem" }}
                                >
                                  {byRisk.medium.every(d => (selectedRowyOnlyDocs[key] || []).includes(d.id)) ? 'Deselect All' : 'Select All'}
                                </Button>
                                {getSelectedCount(key) > 0 && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => {
                                      setDocToDelete({ collection: key, docIds: selectedRowyOnlyDocs[key] || [] });
                                      setDeleteDialogOpen(true);
                                    }}
                                    sx={{ backgroundColor: "#ff9800", color: "#fff" }}
                                  >
                                    Delete Selected ({getSelectedCount(key)})
                                  </Button>
                                )}
                              </Box>
                            </Box>
                            <TableContainer component={Paper} sx={{ backgroundColor: "rgba(255, 152, 0, 0.1)", mb: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell padding="checkbox" sx={{ color: "#fff", fontWeight: "bold" }}>
                                      <Checkbox
                                        checked={byRisk.medium.length > 0 && byRisk.medium.every(d => (selectedRowyOnlyDocs[key] || []).includes(d.id))}
                                        indeterminate={byRisk.medium.some(d => (selectedRowyOnlyDocs[key] || []).includes(d.id)) && !byRisk.medium.every(d => (selectedRowyOnlyDocs[key] || []).includes(d.id))}
                                        onChange={() => handleSelectAllInRisk(key, byRisk.medium, 'medium')}
                                        sx={{ color: "#fff", "&.Mui-checked": { color: "#ff9800" } }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1rem" }}>ID</TableCell>
                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1rem" }}>User ID</TableCell>
                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1rem" }}>Date</TableCell>
                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1rem" }}>Reasons</TableCell>
                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "1rem" }}>Action</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {byRisk.medium.map((doc) => {
                                    const isSelected = (selectedRowyOnlyDocs[key] || []).includes(doc.id);
                                    return (
                                      <TableRow key={doc.id} selected={isSelected}>
                                        <TableCell padding="checkbox">
                                          <Checkbox
                                            checked={isSelected}
                                            onChange={() => handleSelectRowyOnlyDoc(key, doc.id)}
                                            sx={{ color: "#fff", "&.Mui-checked": { color: "#ff9800" } }}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ color: "#aaa", fontFamily: "monospace", fontSize: "0.9rem" }}>
                                          {doc.id}
                                        </TableCell>
                                        <TableCell sx={{ color: "#aaa", fontSize: "0.95rem" }}>{doc.relevantFields.user_id || 'N/A'}</TableCell>
                                        <TableCell sx={{ color: "#aaa", fontSize: "0.95rem" }}>
                                          {doc.relevantFields.date ? new Date(doc.relevantFields.date).toLocaleDateString() : 'N/A'}
                                        </TableCell>
                                        <TableCell sx={{ color: "#aaa", fontSize: "0.9rem" }}>
                                          {doc.riskReasons.join(', ')}
                                        </TableCell>
                                        <TableCell>
                                          <Chip label="REVIEW" size="small" sx={{ backgroundColor: "#ff9800", color: "#fff" }} />
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                        
                        {/* Low Risk Section */}
                        {byRisk.low.length > 0 && (
                          <Box>
                            <Typography variant="h6" sx={{ color: "#2af598", mb: 2 }}>
                              🟢 Low Risk ({byRisk.low.length}) - Recommended: KEEP
                            </Typography>
                            <Alert severity="success" sx={{ backgroundColor: "rgba(42, 245, 152, 0.1)", color: "#2af598" }}>
                              These documents are likely legitimate. They may have been created by the new system or have equivalents in Production.
                            </Alert>
                            {byRisk.low.length <= 5 && (
                              <TableContainer component={Paper} sx={{ backgroundColor: "rgba(42, 245, 152, 0.1)", mt: 2 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>ID</TableCell>
                                      <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>User ID</TableCell>
                                      <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Date</TableCell>
                                      <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Reason</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {byRisk.low.map((doc) => (
                                      <TableRow key={doc.id}>
                                        <TableCell sx={{ color: "#aaa", fontFamily: "monospace", fontSize: "0.8rem" }}>
                                          {doc.id.substring(0, 15)}...
                                        </TableCell>
                                        <TableCell sx={{ color: "#aaa" }}>{doc.relevantFields.user_id || 'N/A'}</TableCell>
                                        <TableCell sx={{ color: "#aaa" }}>
                                          {doc.relevantFields.date ? new Date(doc.relevantFields.date).toLocaleDateString() : 'N/A'}
                                        </TableCell>
                                        <TableCell sx={{ color: "#aaa", fontSize: "0.85rem" }}>
                                          {doc.equivalentReason || doc.riskReasons[0] || 'Low risk'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                          </Box>
                        )}
                        
                            {data.rowyOnlyInRange > rowyOnlyDetails.length && (
                              <Alert severity="info" sx={{ mt: 2, backgroundColor: "rgba(42, 245, 152, 0.1)", color: "#2af598" }}>
                                <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                                  Mostrando {rowyOnlyDetails.length} de {data.rowyOnlyInRange} documentos Rowy-only. 
                                  Para análise completa de todos os documentos, execute: <code style={{background: "#2a2a2a", padding: "2px 4px", fontSize: "0.95rem"}}>node scripts/migration/analyze-discrepancies-complete.js {startDate.format('YYYY-MM-DD')} {endDate.format('YYYY-MM-DD')}</code>
                                </Typography>
                              </Alert>
                            )}
                          </>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              
              {migrationInfo.transactionStatusSync && (
                <Alert severity="info" sx={{ mb: 2, backgroundColor: "rgba(42, 245, 152, 0.1)", color: "#2af598" }}>
                  <Typography variant="body2">
                    <strong>Transaction Status Sync:</strong>
                    <br />
                    • To Update: {migrationInfo.transactionStatusSync.toUpdate || 0} transactions
                    <br />
                    • Already Synced: {migrationInfo.transactionStatusSync.alreadySynced || 0} transactions
                    <br />
                    • Total: {migrationInfo.transactionStatusSync.total || 0} transactions
                  </Typography>
                </Alert>
              )}
              
              {/* Simplified Summary - Only what will be imported */}
              <Alert severity="success" sx={{ backgroundColor: "rgba(42, 245, 152, 0.15)", border: "2px solid #2af598", mb: 3 }}>
                <Typography variant="h6" sx={{ color: "#2af598", mb: 1, fontWeight: "bold" }}>
                  📥 O que será importado da Production para o Rowy:
                </Typography>
                <Box component="ul" sx={{ color: "#fff", pl: 3, mb: 0 }}>
                  {Object.keys(selectedCollections)
                    .filter((key) => selectedCollections[key])
                    .map((key) => {
                      const data = migrationInfo.collections?.[key] || {};
                      const toMigrate = data.toMigrate || 0;
                      if (toMigrate === 0) return null;
                      return (
                        <li key={key} style={{ marginBottom: "8px", fontSize: "1rem" }}>
                          <strong>{collectionLabels[key]}:</strong> {toMigrate} documento{toMigrate !== 1 ? 's' : ''}
                        </li>
                      );
                    })}
                </Box>
                {Object.keys(selectedCollections)
                  .filter((key) => selectedCollections[key])
                  .every((key) => {
                    const data = migrationInfo.collections?.[key] || {};
                    return (data.toMigrate || 0) === 0;
                  }) && (
                  <Typography variant="body1" sx={{ color: "#2af598", fontWeight: "bold" }}>
                    ✅ Nenhum documento novo para importar - todos já existem no Rowy!
                  </Typography>
                )}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.1)", p: 2, flexDirection: "column", gap: 2 }}>
          {/* What Will Happen - Clear Summary Before Execute Button */}
          {!isExecuting && migrationInfo && (
            <Box sx={{ width: "100%", mb: 2 }}>
              <Alert severity="info" sx={{ backgroundColor: "rgba(33, 150, 243, 0.15)", border: "2px solid #2196f3" }}>
                <Typography variant="h6" sx={{ color: "#fff", mb: 2, fontWeight: "bold" }}>
                  ⚙️ O que acontecerá ao clicar "Execute Migration":
                </Typography>
                <Box component="ol" sx={{ color: "#fff", pl: 3, mb: 0, fontSize: "0.95rem", lineHeight: 1.8 }}>
                  <li>
                    <strong>Ler da Production (fiddux-5a1f1):</strong> Buscará documentos do período {startDate.format("DD/MM/YYYY")} a {endDate.format("DD/MM/YYYY")}
                  </li>
                  <li>
                    <strong>Verificar no Rowy:</strong> Checará quais documentos já existem (para evitar duplicatas)
                  </li>
                  <li>
                    <strong>Importar apenas novos:</strong> Adicionará ao Rowy apenas os documentos que ainda não existem
                  </li>
                  <li>
                    <strong>Adicionar flags:</strong> Cada documento importado receberá <code style={{background: "#2a2a2a", padding: "2px 4px"}}>_migrated_from</code> e <code style={{background: "#2a2a2a", padding: "2px 4px"}}>_migrated_at</code>
                  </li>
                </Box>
                <Alert severity="success" sx={{ mt: 2, backgroundColor: "rgba(42, 245, 152, 0.2)", border: "1px solid #2af598" }}>
                  <Typography variant="body2" sx={{ color: "#2af598", fontWeight: "bold" }}>
                    ✅ SEGURO: Production NUNCA será modificada (apenas leitura). Apenas o Rowy receberá novos documentos.
                  </Typography>
                </Alert>
              </Alert>
            </Box>
          )}
          {/* Execution Progress Section */}
          {isExecuting && (
            <Box sx={{ width: "100%", mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" sx={{ color: "#2af598", fontWeight: "bold" }}>
                  {currentStep}
                </Typography>
                <Typography variant="body2" sx={{ color: "#aaa" }}>
                  {executionProgress.toFixed(0)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={executionProgress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#2af598"
                  }
                }} 
              />
              
              {/* Execution Logs */}
              <Box sx={{ 
                mt: 2, 
                maxHeight: "200px", 
                overflowY: "auto",
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                borderRadius: 1,
                p: 1.5,
                fontFamily: "monospace",
                fontSize: "0.85rem"
              }}>
                {executionLogs.map((log, idx) => (
                  <Typography 
                    key={idx}
                    variant="body2"
                    sx={{ 
                      color: log.type === 'error' ? '#f52a2d' : 
                            log.type === 'success' ? '#2af598' : 
                            log.type === 'warning' ? '#ff9800' : '#aaa',
                      mb: 0.5,
                      whiteSpace: "pre-wrap"
                    }}
                  >
                    [{log.timestamp}] {log.message}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
            {isExecuting && canCancel ? (
              <Button 
                onClick={handleCancel} 
                variant="outlined"
                sx={{ 
                  color: "#f52a2d", 
                  borderColor: "#f52a2d",
                  "&:hover": {
                    borderColor: "#f52a2d",
                    backgroundColor: "rgba(245, 42, 45, 0.1)"
                  }
                }}
                fullWidth
              >
                Cancel Migration
              </Button>
            ) : (
              <>
                {!isExecuting && (
                  <Button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Button clicked, calling handleExecute');
                      handleExecute();
                    }} 
                    variant="contained"
                    sx={{ 
                      backgroundColor: "#2af598",
                      color: "#000",
                      fontWeight: "bold",
                      "&:hover": {
                        backgroundColor: "#25d885"
                      }
                    }}
                    fullWidth
                  >
                    Execute Migration
                  </Button>
                )}
                <Button 
                  onClick={() => {
                    setPreviewDialogOpen(false);
                    setIsExecuting(false);
                    setExecutionLogs([]);
                    setExecutionProgress(0);
                  }} 
                  variant="outlined"
                  sx={{ 
                    color: "#aaa",
                    borderColor: "rgba(255, 255, 255, 0.2)",
                    "&:hover": {
                      borderColor: "rgba(255, 255, 255, 0.4)"
                    }
                  }}
                  fullWidth={!isExecuting}
                >
                  {isExecuting ? 'Close (Migration Running)' : 'Close'}
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
            color: "#fff",
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff", borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>
          ⚠️ Execute Migration?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: "#aaa", mb: 2 }}>
            This will migrate data from production (fiddux-5a1f1) to the new database (fidduxai-rowy).
          </Typography>
          <Typography variant="body2" sx={{ color: "#fff", fontWeight: "bold", mb: 1 }}>
            What will happen:
          </Typography>
          <Box component="ul" sx={{ color: "#aaa", pl: 3, mb: 2 }}>
            <li>Import selected collections from production</li>
            <li>Date range: {startDate.format("DD/MM/YYYY")} to {endDate.format("DD/MM/YYYY")}</li>
            <li>{updateBalance ? "Update net_balance for affected users" : "Skip balance updates"}</li>
            <li>{syncTransactionStatus ? "Sync transaction status (Pending → Paid)" : "Skip transaction status sync"}</li>
          </Box>
          <Alert severity="warning" sx={{ backgroundColor: "rgba(255, 152, 0, 0.1)", color: "#ff9800" }}>
            <Typography variant="body2">
              <strong>⚠️ This operation is safe and idempotent.</strong>
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.1)", p: 2 }}>
          <Button 
            onClick={() => setConfirmDialogOpen(false)} 
            sx={{ color: "#aaa" }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmExecute}
            variant="contained"
            sx={{ 
              backgroundColor: "#2af598",
              color: "#000",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#25d885"
              }
            }}
          >
            Yes, Execute Migration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
            color: "#fff",
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff", borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>
          ⚠️ Delete Rowy-only Documents?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: "#aaa", mb: 2 }}>
            You are about to delete {docToDelete?.docIds?.length || 0} document(s) from the Rowy database.
          </Typography>
          <Typography variant="body2" sx={{ color: "#fff", fontWeight: "bold", mb: 1 }}>
            Collection: {docToDelete?.collection ? collectionLabels[docToDelete.collection] : 'Unknown'}
          </Typography>
          <Alert severity="error" sx={{ backgroundColor: "rgba(245, 42, 45, 0.1)", color: "#f52a2d", mb: 2 }}>
            <Typography variant="body2">
              <strong>⚠️ WARNING:</strong> This action cannot be undone. Make sure you have reviewed these documents and confirmed they are test data before proceeding.
            </Typography>
          </Alert>
          <Typography variant="body2" sx={{ color: "#aaa" }}>
            Document IDs to delete:
          </Typography>
          <Box sx={{ 
            backgroundColor: "rgba(0, 0, 0, 0.3)", 
            p: 1.5, 
            borderRadius: 1,
            fontFamily: "monospace",
            fontSize: "0.85rem",
            maxHeight: "200px",
            overflowY: "auto",
            mt: 1
          }}>
            {docToDelete?.docIds?.slice(0, 10).map((id, idx) => (
              <Typography key={idx} sx={{ color: "#aaa", mb: 0.5 }}>
                {idx + 1}. {id}
              </Typography>
            ))}
            {docToDelete?.docIds?.length > 10 && (
              <Typography sx={{ color: "#666", fontStyle: "italic", mt: 1 }}>
                ... and {docToDelete.docIds.length - 10} more
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.1)", p: 2 }}>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setDocToDelete(null);
            }} 
            sx={{ color: "#aaa" }}
          >
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              // TODO: Implement actual deletion via Cloud Function
              Swal.fire({
                icon: 'info',
                title: 'Delete Function Not Implemented',
                text: 'This feature will be implemented. For now, use Firestore console to delete these documents manually.',
                confirmButtonColor: '#2af598'
              });
              setDeleteDialogOpen(false);
              setDocToDelete(null);
              // Clear selections
              setSelectedRowyOnlyDocs({});
            }}
            variant="contained"
            sx={{ 
              backgroundColor: "#f52a2d",
              color: "#fff",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#d02023"
              }
            }}
          >
            Yes, Delete {docToDelete?.docIds?.length || 0} Document(s)
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductionMigrationTool;
